import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, PackageOpen, ArrowLeftRight, Wrench,
  Wallet, Users, FileWarning, Clock, HardDrive, Settings, ClipboardList,
  LogOut, Menu, X, FileDown, Flame, Banknote, Truck, Scale, Fuel,
  Calendar, UserCheck, PhoneCall, Sparkles, Trophy, Boxes, MessageSquare, Receipt, BookOpen, Target, IndianRupee, LineChart, Calculator,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/rate-card", label: "Rate Card", icon: IndianRupee, testid: "nav-rate-card" },
  { to: "/rate-analysis", label: "Rate Analysis", icon: LineChart, testid: "nav-rate-analysis" },
  { to: "/calendar", label: "Calendar & Schedule", icon: Calendar, testid: "nav-calendar" },
  { to: "/checklist", label: "Team Checklist", icon: ClipboardList, testid: "nav-checklist" },
  { to: "/chat", label: "Team Chat", icon: MessageSquare, testid: "nav-chat", salesAllowed: true },
  { to: "/stock-view", label: "Live Stock", icon: Boxes, testid: "nav-stock-view", stockViewer: true, salesAllowed: true },
  { to: "/logs/sales", label: "Sale Logs", icon: ShoppingCart, testid: "nav-sale-logs" },
  { to: "/performance", label: "Customer Performance", icon: Trophy, testid: "nav-performance" },
  { to: "/sale-calculator", label: "Sale Calculator", icon: Calculator, testid: "nav-sale-calc" },
  { to: "/logs/purchases", label: "Purchase Logs", icon: PackageOpen, testid: "nav-purchase-logs", hideForManager: true },
  { to: "/payments-log", label: "Payments Log", icon: Wallet, testid: "nav-payments-log" },
  { section: "Entry" },
  { to: "/entry/purchase", label: "Gas Purchase", icon: PackageOpen, testid: "nav-purchase", hideForManager: true },
  { to: "/entry/sale", label: "Gas Sale", icon: ShoppingCart, testid: "nav-sale" },
  { to: "/entry/transfer", label: "Godown Transfer", icon: ArrowLeftRight, testid: "nav-transfer" },
  { to: "/entry/adjustment", label: "Adjustment / Defective", icon: Wrench, testid: "nav-adjust" },
  { to: "/entry/bulk-purchase", label: "Bulk Purchase (Bullet)", icon: Flame, testid: "nav-bulk-purchase", hideForManager: true },
  { to: "/entry/refill", label: "Refill from Bullet", icon: Flame, testid: "nav-refill" },
  { to: "/entry/payment", label: "Payment Entry", icon: Wallet, testid: "nav-payment" },
  { to: "/expenses", label: "Expenses (Cash)", icon: Banknote, testid: "nav-expenses" },
  { to: "/diesel", label: "Diesel", icon: Fuel, testid: "nav-diesel" },
  { to: "/vehicles", label: "Vehicles", icon: Truck, testid: "nav-vehicles" },
  { section: "Industrial Supplies" },
  { to: "/industrial/stock", label: "Industrial Stock", icon: Boxes, testid: "nav-ind-stock" },
  { to: "/industrial/items", label: "Industrial Items", icon: Wrench, testid: "nav-ind-items" },
  { to: "/industrial/purchase", label: "Industrial Purchase", icon: PackageOpen, testid: "nav-ind-purchase", hideForManager: true },
  { to: "/industrial/sale", label: "Industrial Sale", icon: ShoppingCart, testid: "nav-ind-sale" },
  { section: "Ledger" },
  { to: "/pending/mt", label: "MT Entry Pending", icon: Truck, testid: "nav-pending-mt" },
  { to: "/parties", label: "Customers & Vendors", icon: Users, testid: "nav-parties" },
  { to: "/pending/payments", label: "Pending Payments", icon: Clock, testid: "nav-pending-payments" },
  { to: "/pending/bills", label: "Pending Bill Entry", icon: Receipt, testid: "nav-pending-bills" },
  { to: "/register", label: "Physical Register", icon: BookOpen, testid: "nav-register" },
  { to: "/followups", label: "Payment Follow-ups", icon: PhoneCall, testid: "nav-followups" },
  { to: "/predictions", label: "Order Predictions", icon: Sparkles, testid: "nav-predictions" },
  { to: "/pending/cylinders", label: "Pending Cylinders", icon: FileWarning, testid: "nav-pending-cyl" },
  { to: "/challans", label: "Challans", icon: ClipboardList, testid: "nav-challans" },
  { to: "/challans/gaps", label: "Challan Gaps", icon: ClipboardList, testid: "nav-challan-gaps" },
  { section: "Reports" },
  { to: "/tally", label: "Daily Tally", icon: Scale, testid: "nav-tally" },
  { to: "/stock-movement", label: "Stock Movement", icon: Boxes, testid: "nav-stock-movement", stockViewer: true },
  { to: "/adjustments/log", label: "Adjustment Log", icon: ClipboardList, testid: "nav-adjustment-log" },
  { to: "/reports/daily-closing", label: "Daily Closing", icon: FileDown, testid: "nav-daily" },
  { to: "/downloads", label: "Downloads", icon: FileDown, testid: "nav-downloads" },
  { section: "Staff" },
  { to: "/attendance", label: "Attendance", icon: Calendar, testid: "nav-attendance" },
  { to: "/attendance/summary", label: "Monthly Summary", icon: ClipboardList, testid: "nav-attendance-summary" },
  { to: "/employees", label: "Employees", icon: UserCheck, testid: "nav-employees" },
  { section: "Admin", adminOnly: true },
  { to: "/masters", label: "Masters", icon: HardDrive, testid: "nav-masters", adminOnly: true },
  { to: "/audit", label: "Audit Logs", icon: Settings, testid: "nav-audit", adminOnly: true },
  { to: "/data-transfer", label: "Backup & Transfer", icon: FileDown, testid: "nav-data-transfer", adminOnly: true },
  { section: "Sales" },
  { to: "/sales", label: "Sales & Marketing", icon: Target, testid: "nav-sales", salesAllowed: true },
];

export default function Sidebar({ mobileOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside
        className={`fixed lg:static top-0 left-0 bottom-0 z-50 w-64 bg-[#0A0A0A] text-white flex flex-col transform transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2.5" onClick={onClose}>
            <div className="w-8 h-8 rounded-sm bg-white flex items-center justify-center">
              <Flame size={18} className="text-black" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black text-lg tracking-tight">LPG MANAGER</div>
          </Link>
          <button className="lg:hidden" onClick={onClose}><X size={20} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {items.map((it, i) => {
            if (it.hideForManager && user?.role === "manager") return null;
            if (it.adminOnly && user?.role !== "admin") return null;
            // stock_viewer sees only items explicitly tagged stockViewer
            if (user?.role === "stock_viewer" && !it.stockViewer) return null;
            // sales sees only Sales module + Live Stock + Team Chat
            if (user?.role === "sales") {
              const salesWhitelist = ["/sales", "/stock-view", "/chat"];
              const isAllowed = it.salesAllowed || salesWhitelist.includes(it.to) || (it.section === "Sales");
              if (!isAllowed) return null;
            }
            if (it.section) {
              return (
                <div key={i} className="small-label px-3 pt-4 pb-1 text-gray-500">
                  {it.section}
                </div>
              );
            }
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={it.testid}
                onClick={onClose}
              >
                <Icon size={18} strokeWidth={2.5} className="nav-icon" />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-white/10 px-4 py-3 space-y-2">
          <div className="text-xs">
            <div className="text-white font-semibold">{user?.name || user?.email}</div>
            <div className="text-gray-400 uppercase tracking-wider text-[0.65rem] font-bold">
              {user?.role}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-sm text-gray-300 hover:text-white py-2"
            data-testid="logout-button"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>
    </>
  );
}

export function TopBar({ onMenu, title }) {
  return (
    <header className="h-16 border-b border-gray-200 bg-white flex items-center px-4 lg:px-8 sticky top-0 z-30">
      <button className="lg:hidden mr-3 p-1" onClick={onMenu} data-testid="menu-button"><Menu size={22} /></button>
      <h1 className="font-heading font-black text-lg sm:text-xl lg:text-2xl tracking-tight truncate">{title}</h1>
    </header>
  );
}
